/**
 * WhiteboardPage: 독립 화이트보드 페이지 (/whiteboard).
 *
 * 모바일 네이티브 WebView에서 별도 URL로 열리는 화면이다.
 * URL hash의 state로 collab room 정보를 받고, 문서공유 파일 목록은
 * useWhiteboardPage의 네이티브 message 리스너(FILE_LIST)로만 수신한다.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWhiteboardPage } from '@/hooks/useWhiteboardPage';
import {
  loadDeferredFiles,
  type ExcalidrawAPI,
} from '@/hooks/useWhiteboardCommon';
import {
  MobileDocumentSidebar,
  MobilePermissionPopup,
  MobileSketchToast,
  MobileSketchToolBox,
  type MobileSelectionColorTarget,
  type MobileWhiteboardShape,
  type MobileWhiteboardTool,
} from '@/components/Whiteboard/WhiteboardUIComponents';
import { COLLAB_SERVER_URL, DOC_SHARE_BACKGROUND } from '@/utils/whiteboard/constants';

import '@jitsi/excalidraw/index.css';
import '@/components/Whiteboard/RenewalSchech.css';

let ExcalidrawApp: React.ComponentType<Record<string, unknown>> | null = null;
let mutateElementFn: ((element: Record<string, any>, updates: Record<string, any>) => Record<string, any>) | null = null;
try {
  const mod = await import('@jitsi/excalidraw');
  // @ts-expect-error default export may not exist in the packaged type declarations.
  ExcalidrawApp = mod.ExcalidrawApp ?? mod.default;
  mutateElementFn = (mod as any).mutateElement ?? null;
} catch {
  ExcalidrawApp = null;
}

const WHITEBOARD_VERSION = 'ver.1';
const IS_DEV_SERVER = COLLAB_SERVER_URL.includes('rtctest.wehago.com');

const MOBILE_CANVAS_CONFIG = {
  allowedShapes: ['selector', 'rectangle', 'ellipse', 'diamond', 'arrow', 'line', 'text'],
  hideWelcomeScreen: true,
  hideHelpDialog: true,
  changeViewBackgroundColor: false,
  hideUserList: true,
  hideSearchMenu: true,
  hideExtraTools: true,
  disableShortcuts: true,
  hideIOActions: true,
  hideLibraries: true,
  hideLayers: true,
  hideColorInput: true,
};

interface CollabParticipant {
  isOwner?: boolean;
  jitsiId?: string;
  roomIndex?: number;
  canEdit?: boolean;
}

interface WhiteboardFile {
  id?: string;
  dataURL?: string;
  thumbnailURL?: string;
  [key: string]: unknown;
}

interface PresenterProps {
  roomId: string;
  roomKey: string;
  currentPageNumber: number;
  totalPages: number;
  pdfFileName: string;
  localParticipantName: string;
  jitsiId: string;
  initialZoom: number;
  isViewOnly: boolean;
  isSessionOwner: boolean;
  conferenceId: string;
  fileList: WhiteboardFile[] | null;
  isFileLoading: boolean;
  isDocumentShare: boolean;
  editPermissions: unknown;
  participants: CollabParticipant[];
  handleChangeParticipants: (participants: CollabParticipant[]) => void;
  handlePageChange: (pageNumber: number) => void;
  getWhiteboardInfo: (conferenceId: string) => void;
}

const clampLineWidth = (lineWidth: number) => Math.max(1, Math.min(20, lineWidth));

const getEditableMode = (editPermissions: unknown, participants: CollabParticipant[]) => {
  const otherParticipants = participants.filter((participant) => !participant.isOwner);
  if (otherParticipants.length > 0) {
    return otherParticipants.every((participant) => participant.canEdit === true);
  }
  return editPermissions === null;
};

const DEFAULT_PEN_COLOR = '#2256ff';
const DEFAULT_HIGHLIGHTER_COLOR = '#a8bbff';

const getSelectedElementIds = (appState: Record<string, any>) => Object.keys(appState.selectedElementIds ?? {});

const createVersionNonce = () => Math.floor(Math.random() * 0x7fffffff);

const bumpElementVersion = <T extends Record<string, any>>(element: T, updates: Record<string, any>) => ({
  ...element,
  ...updates,
  updated: Date.now(),
  version: (element.version ?? 0) + 1,
  versionNonce: createVersionNonce(),
});

const getShapeToolType = (shape: MobileWhiteboardShape) => {
  switch (shape) {
    case 'ellipse':
      return 'ellipse';
    case 'diamond':
      return 'diamond';
    case 'line':
    case 'dashedLine':
      return 'line';
    case 'arrow':
    case 'doubleArrow':
      return 'arrow';
    case 'rectangle':
    case 'roundRectangle':
    default:
      return 'rectangle';
  }
};

const getShapeAppState = (shape: MobileWhiteboardShape) => ({
  currentItemRoundness: shape === 'roundRectangle' ? 'round' : 'sharp',
  currentItemStrokeStyle: shape === 'dashedLine' ? 'dashed' : 'solid',
  currentItemStartArrowhead: shape === 'doubleArrow' ? 'arrow' : null,
  currentItemEndArrowhead: shape === 'arrow' || shape === 'doubleArrow' ? 'arrow' : null,
  currentItemArrowType: 'sharp',
});

const getToolType = (tool: MobileWhiteboardTool) => {
  switch (tool) {
    case 'selection':
    case 'template':
      return 'selection';
    case 'eraser':
      return 'eraser';
    case 'shape':
      return 'rectangle';
    case 'text':
      return 'text';
    case 'pen':
    case 'highlighter':
    default:
      return 'freedraw';
  }
};

const getDateFileName = () => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `스케치_${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.png`;
};

const blobToBase64 = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    if (typeof reader.result !== 'string') {
      reject(new Error('Blob 변환 결과가 문자열이 아닙니다.'));
      return;
    }

    const [, base64 = ''] = reader.result.split(',');
    resolve(base64);
  };
  reader.onerror = () => reject(reader.error ?? new Error('Blob을 base64로 변환하지 못했습니다.'));
  reader.readAsDataURL(blob);
});

const sendCaptureToNative = (payload: {
  type: 'WHITEBOARD_CAPTURE';
  fileName: string;
  mimeType: string;
  size: number;
  encoding: 'base64';
  base64: string;
}) => {
  const message = JSON.stringify(payload);
  const nativeWindow = window as Window & {
    ReactNativeWebView?: { postMessage: (message: string) => void };
    webkit?: {
      messageHandlers?: Record<string, { postMessage: (message: string) => void }>;
    };
  };

  if (nativeWindow.ReactNativeWebView?.postMessage) {
    nativeWindow.ReactNativeWebView.postMessage(message);
    return true;
  }

  const webkitHandlers = nativeWindow.webkit?.messageHandlers;
  const webkitHandler =
    webkitHandlers?.whiteboardCapture ??
    webkitHandlers?.whiteboard ??
    webkitHandlers?.nativeApp;

  if (webkitHandler?.postMessage) {
    webkitHandler.postMessage(message);
    return true;
  }

  return false;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const getImageElements = (api: ExcalidrawAPI) => (
  api.getSceneElements().filter((element: any) => element.type === 'image' && !element.isDeleted)
);

const getFileListSignature = (files: WhiteboardFile[] | null | undefined) => (
  files?.map((file) => String(file.id ?? file.thumbnailURL ?? file.dataURL ?? '')).join('|') ?? ''
);

const mobileCanvasStyle = (
  <style>
    {`
      .rtcMobile .rtcMobile_excalidraw {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        z-index: 0;
      }
      .rtcMobile .rtcMobile_excalidraw .excalidraw,
      .rtcMobile .rtcMobile_excalidraw .excalidraw-container {
        width: 100%;
        height: 100%;
      }
      .rtcMobile .App-top-bar,
      .rtcMobile .App-bottom-bar,
      .rtcMobile .App-toolbar-container,
      .rtcMobile .App-menu_bottom,
      .rtcMobile .mobile-misc-tools-container,
      .rtcMobile .dropdown-menu-button,
      .rtcMobile .HintViewer {
        display: none !important;
      }
      .rtcMobile_docview .canvas_inner,
      .rtcMobile_schechMode {
        position: relative;
      }
      .rtcMobile_docview .rtcMobile__controlBox,
      .rtcMobile_schechMode .rtcMobile__controlBox,
      .rtcMobile_docview__schechBtn,
      .rtcMobile .rtcMobile__schechBox {
        z-index: 12;
      }
      .rtcMobile .rtcMobile_popup {
        z-index: 1000;
      }
    `}
  </style>
);

const WhiteboardPagePresenter: React.FC<PresenterProps> = ({
  roomId,
  roomKey,
  currentPageNumber,
  totalPages,
  localParticipantName,
  jitsiId,
  initialZoom,
  isViewOnly,
  isSessionOwner,
  conferenceId,
  fileList,
  isFileLoading,
  isDocumentShare,
  editPermissions,
  participants: initialParticipants,
  handleChangeParticipants,
  handlePageChange,
  getWhiteboardInfo,
}) => {
  const excalidrawApi = useRef<ExcalidrawAPI | null>(null);
  const collabInitRef = useRef(false);
  const toastTimerRef = useRef<number | null>(null);
  const documentFitFrameRef = useRef<number | null>(null);
  const canvasChangeUnsubscribeRef = useRef<(() => void) | null>(null);
  const activeToolRef = useRef<MobileWhiteboardTool>('pen');
  const selectedElementSignatureRef = useRef('');
  const loadedFileSignatureRef = useRef('');
  const isDeletingRef = useRef(false);

  const [participants, setParticipants] = useState<CollabParticipant[]>(initialParticipants);
  const [selectedPenColor, setSelectedPenColor] = useState(DEFAULT_PEN_COLOR);
  const [selectedHighlighterColor, setSelectedHighlighterColor] = useState(DEFAULT_HIGHLIGHTER_COLOR);
  const [selectedFillColor, setSelectedFillColor] = useState(DEFAULT_HIGHLIGHTER_COLOR);
  const [selectedStrokeColor, setSelectedStrokeColor] = useState(DEFAULT_PEN_COLOR);
  const [lineWidth, setLineWidth] = useState(10);
  const [eraserWidth, setEraserWidth] = useState(10);
  const [activeTool, setActiveTool] = useState<MobileWhiteboardTool>('pen');
  const [activeShape, setActiveShape] = useState<MobileWhiteboardShape>('rectangle');
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
  const [isTextSelection, setIsTextSelection] = useState(false);
  const [isToolBoxOpen, setIsToolBoxOpen] = useState(true);
  const [isDocSideOpen, setIsDocSideOpen] = useState(isDocumentShare);
  const [isDocSketchEnabled, setIsDocSketchEnabled] = useState(false);
  const [isPermissionPopupOpen, setIsPermissionPopupOpen] = useState(false);
  const [permissionDraftAllEditable, setPermissionDraftAllEditable] = useState(() => getEditableMode(editPermissions, initialParticipants));
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [backgroundProps] = useState({
    viewBackgroundColor: '#f0f2f7',
    gridModeEnabled: true,
    gridSize: 20,
  });

  const hasFiles = !!fileList?.length;
  const isOwner = isSessionOwner || participants.find((participant) => participant.isOwner)?.jitsiId === jitsiId;
  const isCanvasReadOnly = isViewOnly || (isDocumentShare && !isDocSketchEnabled);
  const hasSelectedElements = selectedElementIds.length > 0;

  const setActiveMobileTool = useCallback((tool: MobileWhiteboardTool) => {
    activeToolRef.current = tool;
    setActiveTool(tool);
  }, []);

  useEffect(() => {
    setParticipants(initialParticipants);
  }, [initialParticipants]);

  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  useEffect(() => {
    if (hasSelectedElements && (activeTool === 'selection' || activeTool === 'template')) {
      setIsToolBoxOpen(true);
    }
  }, [activeTool, hasSelectedElements]);

  useEffect(() => {
    setPermissionDraftAllEditable(getEditableMode(editPermissions, participants));
  }, [editPermissions, participants]);

  const cancelPendingDocumentFit = useCallback(() => {
    if (documentFitFrameRef.current !== null) {
      window.cancelAnimationFrame(documentFitFrameRef.current);
      documentFitFrameRef.current = null;
    }
  }, []);

  const fitDocumentContent = useCallback((api: ExcalidrawAPI) => {
    const imageElements = getImageElements(api);
    if (imageElements.length === 0) return false;

    api.scrollToContent(imageElements, { fitToContent: true, fitToViewport: false });
    return true;
  }, []);

  const scheduleDocumentFit = useCallback((api: ExcalidrawAPI) => {
    cancelPendingDocumentFit();

    const startedAt = performance.now();
    const tryFit = () => {
      if (excalidrawApi.current !== api) return;
      if (fitDocumentContent(api)) return;
      if (performance.now() - startedAt > 1200) return;

      documentFitFrameRef.current = window.requestAnimationFrame(tryFit);
    };

    tryFit();
  }, [cancelPendingDocumentFit, fitDocumentContent]);

  useEffect(() => () => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    canvasChangeUnsubscribeRef.current?.();
    canvasChangeUnsubscribeRef.current = null;
    cancelPendingDocumentFit();
  }, [cancelPendingDocumentFit]);

  useEffect(() => {
    const api = excalidrawApi.current;
    if (!api || !fileList?.length) return;

    const nextSignature = `${roomId}:${getFileListSignature(fileList)}`;
    if (loadedFileSignatureRef.current === nextSignature) return;

    loadedFileSignatureRef.current = nextSignature;
    scheduleDocumentFit(api);
    void loadDeferredFiles(fileList as any, api).then(() => {
      scheduleDocumentFit(api);
    });
  }, [fileList, roomId, scheduleDocumentFit]);

  useLayoutEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    let savedScrollY: number | null = null;
    let isKeyboardVisible = false;
    let maxViewportHeight = window.innerHeight;
    let timeoutId: number;

    const handleViewportResize = () => {
      if (vv.height > maxViewportHeight) {
        maxViewportHeight = vv.height;
      }

      const keyboardHeight = maxViewportHeight - vv.height;
      const keyboardNowVisible = keyboardHeight > 50;

      if (keyboardNowVisible && !isKeyboardVisible) {
        const appState = excalidrawApi.current?.getAppState();
        if (appState) {
          savedScrollY = appState.scrollY as number;
          isKeyboardVisible = true;
        }
      }

      clearTimeout(timeoutId);

      timeoutId = window.setTimeout(() => {
        if (!keyboardNowVisible) {
          if (isKeyboardVisible && savedScrollY !== null) {
            excalidrawApi.current?.updateScene({
              appState: { scrollY: savedScrollY }
            });
            savedScrollY = null;
          }
          isKeyboardVisible = false;
          return;
        }

        // 키보드가 올라온 경우
        const appState = excalidrawApi.current?.getAppState();
        if (!appState) return;

        const textarea = document.querySelector('.excalidraw-textEditorContainer textarea') as HTMLElement | null;
        if (!textarea) return;

        const rect = textarea.getBoundingClientRect();
        
        const visibleBottom = vv.height - 100; 

        if (rect.bottom > visibleBottom) {
          const pixelDiff = rect.bottom - visibleBottom;
          const zoom = (appState.zoom as any)?.value ?? 1;

          excalidrawApi.current?.updateScene({
            appState: {
              scrollY: (appState.scrollY as number) - (pixelDiff / zoom)
            }
          });
        }
      }, 150);
    };

    vv.addEventListener('resize', handleViewportResize);
    
    return () => {
      clearTimeout(timeoutId);
      if ((window as any).collabAPI?.portal?.socket) {
        (window as any).collabAPI.portal.close();
      }
      vv.removeEventListener('resize', handleViewportResize);
    };
  }, []);

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => setToastMessage(null), 3000);
  }, []);

  const updateCurrentItemStyle = useCallback((
    tool = activeTool,
    options: { color?: string; width?: number; eraserWidth?: number; shape?: MobileWhiteboardShape } = {},
  ) => {
    const shape = options.shape ?? activeShape;
    const isHighlighter = tool === 'highlighter';
    const strokeColor = options.color ?? (isHighlighter ? selectedHighlighterColor : selectedPenColor);
    const strokeWidth = tool === 'eraser'
      ? (options.eraserWidth ?? eraserWidth)
      : (options.width ?? lineWidth);

    excalidrawApi.current?.updateScene({
      appState: {
        currentItemStrokeColor: strokeColor,
        currentItemBackgroundColor: 'transparent',
        currentItemFillStyle: 'solid',
        currentItemStrokeWidth: isHighlighter ? Math.max(strokeWidth, 10) : strokeWidth,
        currentItemOpacity: isHighlighter ? 45 : 100,
        currentItemRoughness: 0,
        ...(tool === 'shape' ? getShapeAppState(shape) : {}),
      },
    });
  }, [activeShape, activeTool, eraserWidth, lineWidth, selectedHighlighterColor, selectedPenColor]);

  const selectMobileTool = useCallback((tool: MobileWhiteboardTool) => {
    const toggleableTools: MobileWhiteboardTool[] = ['pen', 'highlighter', 'eraser', 'text'];
    const isToggleOff = tool === activeTool && toggleableTools.includes(tool);
    const nextTool = isToggleOff ? 'selection' : tool === 'template' ? 'selection' : tool;
    setActiveMobileTool(nextTool);
    setIsToolBoxOpen(true);

    if (nextTool === 'shape') {
      setActiveShape('rectangle');
      updateCurrentItemStyle('shape', { shape: 'rectangle' });
      excalidrawApi.current?.setActiveTool({ type: getShapeToolType('rectangle') });
      return;
    }

    if (nextTool === 'selection') {
      excalidrawApi.current?.setActiveTool({ type: 'selection' });
      return;
    }

    updateCurrentItemStyle(nextTool);
    excalidrawApi.current?.setActiveTool({ type: getToolType(nextTool) });
  }, [activeTool, setActiveMobileTool, updateCurrentItemStyle]);

  const handleSelectShape = useCallback((shape: MobileWhiteboardShape) => {
    setActiveShape(shape);
    setActiveMobileTool('shape');
    setIsToolBoxOpen(true);
    updateCurrentItemStyle('shape', { shape });
    excalidrawApi.current?.setActiveTool({ type: getShapeToolType(shape) });
  }, [setActiveMobileTool, updateCurrentItemStyle]);

  const handleSelectPenColor = useCallback((color: string) => {
    setSelectedPenColor(color);
    updateCurrentItemStyle('pen', { color });
  }, [updateCurrentItemStyle]);

  const handleSelectHighlighterColor = useCallback((color: string) => {
    setSelectedHighlighterColor(color);
    updateCurrentItemStyle('highlighter', { color });
  }, [updateCurrentItemStyle]);

  const handleChangeLineWidth = useCallback((nextLineWidth: number) => {
    const normalizedLineWidth = clampLineWidth(nextLineWidth);
    setLineWidth(normalizedLineWidth);
    updateCurrentItemStyle(activeToolRef.current, { width: normalizedLineWidth });
  }, [updateCurrentItemStyle]);

  const handleChangeEraserWidth = useCallback((nextLineWidth: number) => {
    const normalizedLineWidth = clampLineWidth(nextLineWidth);
    setEraserWidth(normalizedLineWidth);
    updateCurrentItemStyle('eraser', { eraserWidth: normalizedLineWidth });
  }, [updateCurrentItemStyle]);

  const updateSelectedElements = useCallback((getUpdates: (element: Record<string, any>) => Record<string, any>) => {
    const api = excalidrawApi.current;
    if (!api) return false;

    const appState = api.getAppState() as Record<string, any>;
    const selectedIds = appState.selectedElementIds ?? {};
    let didUpdate = false;
    const nextElements = (api.getSceneElements() as Record<string, any>[]).map((element) => {
      if (!selectedIds[element.id] || element.isDeleted) return element;

      const updates = getUpdates(element);
      const hasChanges = Object.entries(updates).some(([key, value]) => element[key] !== value);
      if (!hasChanges) return element;

      didUpdate = true;
      return bumpElementVersion(element, updates);
    });

    if (!didUpdate) return false;

    (api.updateScene as any)({
      elements: nextElements,
      appState: { selectedElementIds: appState.selectedElementIds },
      captureUpdate: 'IMMEDIATELY',
    });
    return true;
  }, []);

  const handleChangeSelectedElementColor = useCallback((target: MobileSelectionColorTarget, color: string) => {
    if (target === 'fill') {
      setSelectedFillColor(color);
      updateSelectedElements(() => ({ backgroundColor: color, fillStyle: 'solid' }));
      return;
    }

    setSelectedStrokeColor(color);
    updateSelectedElements(() => ({ strokeColor: color }));
  }, [updateSelectedElements]);

  const handleDeleteSelection = useCallback(() => {
    if (isDeletingRef.current) return;

    const api = excalidrawApi.current;
    if (!api) return;

    const appState = api.getAppState() as Record<string, any>;
    const selectedIds = appState.selectedElementIds ?? {};
    if (Object.keys(selectedIds).length === 0) return;

    isDeletingRef.current = true;
    window.setTimeout(() => { isDeletingRef.current = false; }, 400);

    const elements = (api as any).getSceneElementsIncludingDeleted() as Record<string, any>[];
    let didDelete = false;

    if (mutateElementFn) {
      elements.forEach((element) => {
        if (selectedIds[element.id] && !element.isDeleted) {
          mutateElementFn!(element, { isDeleted: true });
          didDelete = true;
        }
      });
    }

    if (!didDelete) {
      const wrapper = document.getElementById('excalidraw-wrapper');
      const target =
        wrapper?.querySelector('.excalidraw canvas') ??
        wrapper?.querySelector('.excalidraw') ??
        wrapper;
      if (target) {
        const fire = (type: 'keydown' | 'keyup') =>
          target.dispatchEvent(new KeyboardEvent(type, {
            key: 'Delete',
            code: 'Delete',
            keyCode: 46,
            which: 46,
            bubbles: true,
            cancelable: true,
          } as any));
        fire('keydown');
        fire('keyup');
      }
    }

    // 선택 상태 정리 (mutateElement는 appState.selectedElementIds를 건드리지 않음)
    selectedElementSignatureRef.current = '';
    setSelectedElementIds([]);
    setIsTextSelection(false);
    if (didDelete) {
      // mutateElement 경로일 때만 elements를 함께 넘겨 Scene.replaceAllElements()를 강제 실행 —
      // 이게 없으면 mutate된 결과가 Scene의 nonDeletedElements 캐시에 반영되지 않아 로컬 화면에서
      // 방금 지운 요소가 계속 보이다가 다른 remote 업데이트가 들어올 때에야 사라진다.
      // (keydown 폴백 경로에서는 네이티브 액션이 자체적으로 scene을 갱신하므로 elements를 넘기지 않는다 —
      // 여기서 쓰는 elements는 mutateElement 이전 스냅샷이라 그대로 넘기면 네이티브 삭제 결과를 덮어씀)
      (api.updateScene as any)({
        elements,
        appState: { selectedElementIds: {} },
        captureUpdate: 'IMMEDIATELY',
      });
    } else {
      (api.updateScene as any)({
        appState: { selectedElementIds: {} },
        captureUpdate: 'EVENTUALLY',
      });
    }
  }, []);

  const handleCanvasChange = useCallback((elements: readonly unknown[], appState: Record<string, any>) => {
    if (!appState) return;

    const nextSelectedElementIds = getSelectedElementIds(appState);
    const nextSignature = nextSelectedElementIds.join('|');
    if (selectedElementSignatureRef.current === nextSignature) return;

    selectedElementSignatureRef.current = nextSignature;
    setSelectedElementIds(nextSelectedElementIds);

    const selectedElements = (elements as Record<string, any>[]).filter(
      (element) => nextSelectedElementIds.includes(element.id) && !element.isDeleted,
    );
    const selectedElement = selectedElements[0];

    setIsTextSelection(selectedElements.length > 0 && selectedElements.every((element) => element.type === 'text'));

    if (selectedElement) {
      setSelectedFillColor(selectedElement.backgroundColor || DEFAULT_HIGHLIGHTER_COLOR);
      setSelectedStrokeColor(selectedElement.strokeColor || DEFAULT_PEN_COLOR);
      if (activeToolRef.current === 'selection' || activeToolRef.current === 'template') {
        setIsToolBoxOpen(true);
      }
    }
  }, []);

  const handleCloseToolBox = useCallback(() => {
    if (isDocumentShare) {
      setIsDocSketchEnabled(false);
      setActiveMobileTool('selection');
      excalidrawApi.current?.setActiveTool({ type: 'selection' });
      return;
    }

    setIsToolBoxOpen(false);
  }, [isDocumentShare, setActiveMobileTool]);

  const handleEnableDocumentSketch = useCallback(() => {
    setIsDocSketchEnabled(true);
    setIsToolBoxOpen(true);
    selectMobileTool('pen');
  }, [selectMobileTool]);

  const handleUndo = useCallback(() => {
    excalidrawApi.current?.undo?.();
  }, []);

  const handleRedo = useCallback(() => {
    excalidrawApi.current?.redo?.();
  }, []);

  const handleCapture = useCallback(async () => {
    const api = excalidrawApi.current;
    if (!api) return;

    try {
      const mod = await import('@jitsi/excalidraw');
      const exportToBlob = (mod as any).exportToBlob;
      const blob = await exportToBlob({
        elements: api.getSceneElements(),
        appState: {
          ...api.getAppState(),
          exportWithDarkMode: false,
          exportBackground: true,
        },
        files: api.getFiles(),
      });

      const fileName = getDateFileName();
      const base64 = await blobToBase64(blob);
      const isSentToNative = sendCaptureToNative({
        type: 'WHITEBOARD_CAPTURE',
        fileName,
        mimeType: blob.type || 'image/png',
        size: blob.size,
        encoding: 'base64',
        base64,
      });

      if (!isSentToNative) {
        downloadBlob(blob, fileName);
      }

      showToast('현재 스케치 화면이 캡쳐되었습니다.');
    } catch (error) {
      console.error('[WhiteboardPage] 캡쳐 실패:', error);
      showToast('캡쳐에 실패했습니다. 다시 시도해주세요.');
    }
  }, [showToast]);

  const handleApplyPermission = useCallback(async () => {
    if (!isOwner) return;

    try {
      const response = await fetch(`${COLLAB_SERVER_URL}/api/participants-permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conferenceId,
          editPermissions: permissionDraftAllEditable ? null : [jitsiId],
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setIsPermissionPopupOpen(false);
      getWhiteboardInfo(conferenceId);
      showToast(
        permissionDraftAllEditable
          ? '공동 편집이 활성화되었어요. 함께 스케치 해보세요!'
          : '뷰어 모드가 적용되었습니다.',
      );
    } catch (error) {
      console.error('[WhiteboardPage] 권한 변경 실패:', error);
      showToast('업데이트 내용을 동기화할 수 없습니다. 네트워크 연결을 확인해주세요.');
    }
  }, [conferenceId, getWhiteboardInfo, isOwner, jitsiId, permissionDraftAllEditable, showToast]);

  const handleParticipantsChanged = useCallback((nextParticipants: CollabParticipant[]) => {
    setParticipants([...nextParticipants]);
    handleChangeParticipants(nextParticipants);
  }, [handleChangeParticipants]);

  const initialAppState = useMemo(() => {
    if (isDocumentShare || hasFiles) {
      return { ...DOC_SHARE_BACKGROUND };
    }

    return {
      viewBackgroundColor: backgroundProps.viewBackgroundColor,
      gridModeEnabled: backgroundProps.gridModeEnabled,
      gridSize: backgroundProps.gridSize,
      currentItemStrokeColor: selectedPenColor,
      currentItemStrokeWidth: lineWidth,
    };
  }, [backgroundProps, hasFiles, isDocumentShare, lineWidth, selectedPenColor]);

  if (!ExcalidrawApp) {
    return (
      <div id="wrap">
        <div className="rtcMobile pretendard_font">
          <div className="rtcCustoms_wb__loading">
            <p>@jitsi/excalidraw 패키지가 설치되지 않았습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  const excalidrawElement = (
    <div
      id="excalidraw-wrapper"
      className="excalidraw-wrapper whiteboardEx--mobile rtcMobile_excalidraw"
    >
      <ExcalidrawApp
        key={`mobile-whiteboard-${roomId}-${currentPageNumber}`}
        collabServerUrl={COLLAB_SERVER_URL}
        storageBackendUrl={`${COLLAB_SERVER_URL}/api`}
        conferenceId={conferenceId}
        localParticipantName={localParticipantName || '익명 사용자'}
        collabDetails={{ roomId, roomKey }}
        jitsiId={jitsiId}
        meetingDetails={{
          sessionId: roomId,
          roomJid: roomId,
          jwt: '',
          jid: jitsiId,
          token: 'dummy-token',
        }}
        onChangeActiveTool={(tool: { type: string }) => {
          if (tool.type === 'selection') {
            setActiveMobileTool('selection');
          } else if (tool.type === 'eraser') {
            setActiveMobileTool('eraser');
          } else if (tool.type === 'text') {
            setActiveMobileTool('text');
          } else if (tool.type === 'freedraw') {
            setActiveMobileTool(activeToolRef.current === 'highlighter' ? 'highlighter' : 'pen');
          } else if (['rectangle', 'ellipse', 'diamond', 'line', 'arrow'].includes(tool.type)) {
            setActiveMobileTool('shape');
          }
        }}
        onChangeParticipants={handleParticipantsChanged}
        excalidraw={{
          aiEnabled: false,
          initialZoom,
          isCollaborating: true,
          langCode: 'ko-KR',
          initialData: {
            appState: initialAppState,
          },
          UIOptions: {
            welcomeScreen: false,
            canvasActions: MOBILE_CANVAS_CONFIG,
          },
          viewModeEnabled: isCanvasReadOnly,
          zenModeEnabled: isCanvasReadOnly,
        }}
        autoFocus={!isCanvasReadOnly}
        autoResize
        getExcalidrawAPI={(api: ExcalidrawAPI) => {
          if (excalidrawApi.current === api) return;
          (window as any).excalidrawAPI = api;
          excalidrawApi.current = api;
          updateCurrentItemStyle(activeTool);

          canvasChangeUnsubscribeRef.current?.();
          canvasChangeUnsubscribeRef.current = null;
          if (typeof api.onChange === 'function') {
            canvasChangeUnsubscribeRef.current = (api.onChange as unknown as (
              cb: (elements: readonly unknown[], appState: Record<string, any>) => void,
            ) => () => void)(handleCanvasChange);
          }

          if (fileList?.length) {
            loadedFileSignatureRef.current = `${roomId}:${getFileListSignature(fileList)}`;
            scheduleDocumentFit(api);
            void loadDeferredFiles(fileList as any, api).then(() => {
              scheduleDocumentFit(api);
            });
          } else {
            window.requestAnimationFrame(() => api.scrollToContent());
          }
        }}
        getCollabAPI={(collabAPI: { setUsername: (name: string) => void }) => {
          (window as any).collabAPI = collabAPI;
          collabAPI.setUsername(localParticipantName || '익명 사용자');
          if (!collabInitRef.current) {
            collabInitRef.current = true;
            getWhiteboardInfo(conferenceId);
          }
        }}
      />
    </div>
  );

  const controls = !isViewOnly ? (
    <div className="rtcMobile__controlBox">
      <button type="button" className="controlBox__btn ico_left active" onClick={handleUndo}>
        <span className="blind">이전으로 복귀</span>
      </button>
      <button type="button" className="controlBox__btn ico_right active" onClick={handleRedo}>
        <span className="blind">재실행</span>
      </button>
      <span className="controlBox__bar" />
      <button type="button" className="controlBox__btn ico_capture" onClick={handleCapture}>
        <span className="blind">캡쳐</span>
      </button>
    </div>
  ) : null;

  const permissionButton = isOwner ? (
    <div className="rtcMobile__controlBox typeLeft">
      <button type="button" className="controlBox__btn ico_list" onClick={() => setIsPermissionPopupOpen(true)}>
        <span className="blind">메뉴 보기</span>
      </button>
    </div>
  ) : null;

  const buildVersionBadge = (
    <div
      style={{
        position: 'fixed',
        bottom: 4,
        right: 6,
        zIndex: 999999,
        color: '#fff',
        background: 'rgba(0,0,0,0.55)',
        borderRadius: 4,
        padding: '2px 5px',
        fontSize: 9,
        lineHeight: 1.2,
        fontFamily: 'monospace',
        pointerEvents: 'none',
      }}
    >
      {WHITEBOARD_VERSION}
    </div>
  );

  if (isDocumentShare) {
    return (
      <div id="wrap">
        <div className="rtcMobile pretendard_font">
          {IS_DEV_SERVER && buildVersionBadge}
          {mobileCanvasStyle}
          <div className={`rtcMobile_docview ${isDocSideOpen ? 'sideOpen' : ''}`}>
            <div className="canvas_area">
              <div className="canvas_inner">
                {excalidrawElement}
                {isFileLoading && (
                  <div className="rtcMobile_schechMode__loading" style={{ zIndex: 13 }}>
                    <p className="loadingText">스케치를 불러오는중...</p>
                    <div className="loadingBox">
                      <span className="loadingBox__progress" style={{ width: '50%' }} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button type="button" className="rtcMobile_docview__pageBtn" onClick={() => setIsDocSideOpen((value) => !value)}>
              <span className="num">{currentPageNumber}</span><span className="numMax">/{totalPages}</span>
            </button>

            {!isViewOnly && !isDocSketchEnabled && (
              <button type="button" className="rtcMobile_docview__schechBtn" onClick={handleEnableDocumentSketch}>
                <span className="blind">그림 그리기</span>
              </button>
            )}

            {isDocSketchEnabled && controls}

            {isDocSketchEnabled && !isViewOnly && (
              <MobileSketchToolBox
                activeTool={activeTool}
                activeShape={activeShape}
                selectedPenColor={selectedPenColor}
                selectedHighlighterColor={selectedHighlighterColor}
                selectedFillColor={selectedFillColor}
                selectedStrokeColor={selectedStrokeColor}
                lineWidth={lineWidth}
                eraserWidth={eraserWidth}
                hasSelection={hasSelectedElements}
                isTextSelection={isTextSelection}
                isOpen={isToolBoxOpen}
                variant="document"
                onSelectTool={selectMobileTool}
                onSelectShape={handleSelectShape}
                onSelectPenColor={handleSelectPenColor}
                onSelectHighlighterColor={handleSelectHighlighterColor}
                onChangeSelectedElementColor={handleChangeSelectedElementColor}
                onChangeLineWidth={handleChangeLineWidth}
                onChangeEraserWidth={handleChangeEraserWidth}
                onDeleteSelection={handleDeleteSelection}
                onClose={handleCloseToolBox}
              />
            )}

            <MobileDocumentSidebar
              currentPage={currentPageNumber}
              files={fileList ?? []}
              onClickPageNumber={handlePageChange}
            />
          </div>

          <MobileSketchToast message={toastMessage} />
        </div>
      </div>
    );
  }

  return (
    <div id="wrap">
      <div className="rtcMobile pretendard_font">
        {IS_DEV_SERVER && buildVersionBadge}
        {mobileCanvasStyle}
        <div className="rtcMobile_schechMode">
          {excalidrawElement}
          {controls}
          {permissionButton}
        </div>

        {!isViewOnly && (
          <MobileSketchToolBox
            activeTool={activeTool}
            activeShape={activeShape}
            selectedPenColor={selectedPenColor}
            selectedHighlighterColor={selectedHighlighterColor}
            selectedFillColor={selectedFillColor}
            selectedStrokeColor={selectedStrokeColor}
            lineWidth={lineWidth}
            eraserWidth={eraserWidth}
            hasSelection={hasSelectedElements}
            isTextSelection={isTextSelection}
            isOpen={isToolBoxOpen}
            variant="sketch"
            onSelectTool={selectMobileTool}
            onSelectShape={handleSelectShape}
            onSelectPenColor={handleSelectPenColor}
            onSelectHighlighterColor={handleSelectHighlighterColor}
            onChangeSelectedElementColor={handleChangeSelectedElementColor}
            onChangeLineWidth={handleChangeLineWidth}
            onChangeEraserWidth={handleChangeEraserWidth}
            onDeleteSelection={handleDeleteSelection}
            onClose={handleCloseToolBox}
          />
        )}

        <MobilePermissionPopup
          isOpen={isPermissionPopupOpen}
          isAllEditable={permissionDraftAllEditable}
          onChangeMode={setPermissionDraftAllEditable}
          onApply={handleApplyPermission}
        />
        <MobileSketchToast message={toastMessage} />
      </div>
    </div>
  );
};

const WhiteboardPage: React.FC = () => {
  const hookState = useWhiteboardPage({ isViewOnly: true });

  if (!hookState.isValidState) {
    return <div className="rtcCustoms_wb__placeholder">state 값을 찾을 수 없습니다.</div>;
  }

  return (
    <WhiteboardPagePresenter
      key={`WhiteboardPagePresenter_${hookState.currentRoomInfo.roomId}_${hookState.currentPageNumber}`}
      roomId={hookState.currentRoomInfo.roomId}
      roomKey={hookState.currentRoomInfo.roomKey}
      currentPageNumber={hookState.currentPageNumber}
      totalPages={hookState.totalPages}
      pdfFileName={hookState.pdfFileName}
      localParticipantName={hookState.localParticipantName}
      isViewOnly={hookState.isViewOnly}
      isSessionOwner={hookState.isSessionOwner}
      initialZoom={hookState.initialZoom}
      jitsiId={hookState.jitsiId}
      conferenceId={hookState.conferenceId}
      fileList={hookState.fileList.length > 0 ? hookState.fileList : null}
      isFileLoading={hookState.isFileLoading}
      isDocumentShare={hookState.isViewPageInfo}
      editPermissions={hookState.editPermissions}
      participants={hookState.participants}
      handleChangeParticipants={hookState.onChangeParticipants}
      handlePageChange={hookState.setCurrentPageNumber}
      getWhiteboardInfo={hookState.getWhiteboardInfo}
    />
  );
};

export default WhiteboardPage;
